import { createContext, useContext, useState, useCallback, useEffect, useRef } from 'react'
import { useAuth } from './AuthContext'
import { notificacionService } from '../services/notificacionService'

const NotificationContext = createContext(null)

function toBadgeCount(value) {
  const parsed = Number(value)
  if (!Number.isFinite(parsed)) return 0
  return parsed > 0 ? Math.floor(parsed) : 0
}

async function applyAppBadge(count) {
  if (typeof navigator === 'undefined') return
  const safeCount = toBadgeCount(count)
  try {
    if (safeCount > 0 && typeof navigator.setAppBadge === 'function') {
      await navigator.setAppBadge(safeCount)
      return
    }
    if (typeof navigator.clearAppBadge === 'function') {
      await navigator.clearAppBadge()
    }
  } catch (error) {
    // Safari/iOS puede no soportar Badging API todavía.
    console.debug('Badging API no disponible o bloqueada:', error?.message || error)
  }
}

export const NotificationProvider = ({ children }) => {
  const { user } = useAuth()
  const [notificaciones, setNotificaciones] = useState([])
  const [contadorNoLeidas, setContadorNoLeidas] = useState(0)
  const [loading, setLoading] = useState(false)
  const refreshListenersRef = useRef(new Set())
  const pollingIntervalRef = useRef(null)
  const isPollingRef = useRef(false) // Flag para evitar peticiones simultáneas

  // Función para notificar a todos los listeners que deben refrescar
  const notifyRefresh = useCallback(() => {
    refreshListenersRef.current.forEach(listener => {
      if (typeof listener === 'function') {
        listener()
      }
    })
  }, [])

  // Registrar un listener para refrescar
  const registerRefreshListener = useCallback((listener) => {
    refreshListenersRef.current.add(listener)
    return () => {
      refreshListenersRef.current.delete(listener)
    }
  }, [])

  // Obtener contador de no leídas
  const fetchContador = useCallback(async () => {
    if (!user || isPollingRef.current) {
      return // Evitar peticiones simultáneas
    }
    
    try {
      isPollingRef.current = true
      const response = await notificacionService.getContadorNoLeidas()
      if (response.success) {
        setContadorNoLeidas(response.data?.count || response.data || 0)
      } else {
        setContadorNoLeidas(0)
      }
    } catch (err) {
      // No romper la aplicación si hay error (ej: 429 Too Many Requests)
      const status = err.response?.status
      if (status === 429) {
        console.warn('⚠️ Demasiadas peticiones al contador de notificaciones, manteniendo valor actual')
        // Mantener el valor actual en lugar de ponerlo en 0
      } else {
        console.error('Error fetching contador:', err)
        setContadorNoLeidas(0)
      }
    } finally {
      isPollingRef.current = false
    }
  }, [user])

  // Función para marcar una notificación como leída
  const marcarLeida = useCallback(async (notificacionId) => {
    try {
      const response = await notificacionService.marcarLeida(notificacionId)
      if (response.success) {
        // Actualizar estado local
        setNotificaciones(prev => 
          prev.map(n => {
            const id = n.id_notificacion || n.idNotificacion
            if (id === notificacionId) {
              return { ...n, leida: true }
            }
            return n
          })
        )
        // Actualizar contador (sin notificar listeners para evitar cascadas)
        await fetchContador()
        // Notificar a todos los listeners con un pequeño delay para evitar peticiones simultáneas
        setTimeout(() => {
          notifyRefresh()
        }, 300)
        return true
      }
      return false
    } catch (err) {
      console.error('Error marcar leida:', err)
      return false
    }
  }, [fetchContador, notifyRefresh])

  // Función para marcar todas como leídas
  const marcarTodasLeidas = useCallback(async () => {
    try {
      const response = await notificacionService.marcarTodasLeidas()
      if (response.success) {
        // Actualizar estado local
        setNotificaciones(prev => prev.map(n => ({ ...n, leida: true })))
        // Actualizar contador (sin notificar listeners inmediatamente)
        await fetchContador()
        // Notificar a todos los listeners con un pequeño delay
        setTimeout(() => {
          notifyRefresh()
        }, 300)
        return true
      }
      return false
    } catch (err) {
      console.error('Error marcar todas leidas:', err)
      return false
    }
  }, [fetchContador, notifyRefresh])

  /** Ocultar todas las notificaciones del usuario (soft delete) */
  const ocultarTodasNotificaciones = useCallback(async () => {
    try {
      const response = await notificacionService.deleteSoftAll()
      if (response?.success) {
        // Limpiar estado local
        setNotificaciones([])
        await fetchContador()
        setTimeout(() => {
          notifyRefresh()
        }, 300)
        return true
      }
      return false
    } catch (err) {
      console.error('Error ocultar todas notificaciones:', err)
      return false
    }
  }, [fetchContador, notifyRefresh])

  // Función para eliminar una notificación (definitivo)
  const eliminarNotificacion = useCallback(async (notificacionId) => {
    try {
      const response = await notificacionService.delete(notificacionId)
      if (response.success) {
        // Actualizar estado local
        setNotificaciones(prev => 
          prev.filter(n => {
            const id = n.id_notificacion || n.idNotificacion
            return id !== notificacionId
          })
        )
        // Actualizar contador (sin notificar listeners inmediatamente)
        await fetchContador()
        // Notificar a todos los listeners con un pequeño delay
        setTimeout(() => {
          notifyRefresh()
        }, 300)
        return true
      }
      return false
    } catch (err) {
      console.error('Error eliminar notificacion:', err)
      return false
    }
  }, [fetchContador, notifyRefresh])

  /** Ocultar sin borrar del servidor (soft delete) — mismo efecto en UI que eliminar */
  const ocultarNotificacion = useCallback(async (notificacionId) => {
    try {
      const response = await notificacionService.deleteSoft(notificacionId)
      if (response?.success) {
        setNotificaciones(prev =>
          prev.filter(n => {
            const id = n.id_notificacion || n.idNotificacion
            return id !== notificacionId
          })
        )
        await fetchContador()
        setTimeout(() => {
          notifyRefresh()
        }, 300)
        return true
      }
      return false
    } catch (err) {
      console.error('Error ocultar notificacion:', err)
      return false
    }
  }, [fetchContador, notifyRefresh])

  // Función para refrescar todo
  const refresh = useCallback(async () => {
    await fetchContador()
    notifyRefresh()
  }, [fetchContador, notifyRefresh])

  // Push foreground/background: refrescar contador + listas (con reintento por race del backend)
  useEffect(() => {
    if (!user) return undefined

    const timeouts = []
    let lastScheduledAt = 0

    const scheduleRefresh = () => {
      const now = Date.now()
      // Foreground dispara notification-received + push-remote-received; dedupe
      if (now - lastScheduledAt < 400) return
      lastScheduledAt = now
      ;[800, 2200].forEach((delay) => {
        timeouts.push(
          setTimeout(() => {
            refresh()
          }, delay)
        )
      })
    }

    window.addEventListener('notification-received', scheduleRefresh)
    window.addEventListener('push-remote-received', scheduleRefresh)

    const onVisibility = () => {
      if (document.visibilityState === 'visible') {
        refresh()
      }
    }
    document.addEventListener('visibilitychange', onVisibility)

    return () => {
      timeouts.forEach(clearTimeout)
      window.removeEventListener('notification-received', scheduleRefresh)
      window.removeEventListener('push-remote-received', scheduleRefresh)
      document.removeEventListener('visibilitychange', onVisibility)
    }
  }, [user, refresh])

  // Cargar contador solo cuando cambia el usuario - SIN polling automático
  // El polling se deshabilitó para evitar demasiadas peticiones
  // Los datos se actualizarán solo cuando:
  // 1. El usuario cambie
  // 2. Se marque una notificación como leída
  // 3. Se elimine una notificación
  // 4. Se llame manualmente a refresh()
  // 5. Se reciba una notificación push (eventos 'notification-received' / 'push-remote-received')
  // 6. La app vuelva a primer plano (visibilitychange)
  useEffect(() => {
    if (user) {
      // Cargar contador inicial con un delay para evitar peticiones simultáneas al cargar
      const initialTimeout = setTimeout(() => {
        fetchContador()
      }, 1000) // Reducido a 1 segundo
      
      return () => {
        clearTimeout(initialTimeout)
      }
    } else {
      // Limpiar cuando no hay usuario
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current)
        pollingIntervalRef.current = null
      }
      setContadorNoLeidas(0)
      setNotificaciones([])
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]) // Solo depende de user, no de fetchContador para evitar recreaciones

  useEffect(() => {
    const badgeCount = user ? contadorNoLeidas : 0
    applyAppBadge(badgeCount)

    if (typeof navigator !== 'undefined' && 'serviceWorker' in navigator) {
      navigator.serviceWorker.ready
        .then((registration) => {
          registration.active?.postMessage({
            type: 'SET_BADGE_COUNT',
            count: toBadgeCount(badgeCount)
          })
        })
        .catch(() => {
          // Ignorar si el SW no está listo aún.
        })
    }
  }, [contadorNoLeidas, user])

  const value = {
    notificaciones,
    setNotificaciones,
    contadorNoLeidas,
    setContadorNoLeidas,
    loading,
    setLoading,
    fetchContador,
    marcarLeida,
    marcarTodasLeidas,
    ocultarTodasNotificaciones,
    eliminarNotificacion,
    ocultarNotificacion,
    refresh,
    registerRefreshListener,
    notifyRefresh
  }

  return (
    <NotificationContext.Provider value={value}>
      {children}
    </NotificationContext.Provider>
  )
}

export const useNotificationRefresh = () => {
  const context = useContext(NotificationContext)
  if (!context) {
    // Si no hay contexto, retornar funciones no-op y valores por defecto
    return { 
      refresh: () => {},
      registerRefreshListener: () => () => {},
      contadorNoLeidas: 0,
      marcarLeida: async () => false,
      marcarTodasLeidas: async () => false,
      ocultarTodasNotificaciones: async () => false,
      eliminarNotificacion: async () => false,
      ocultarNotificacion: async () => false,
      notificaciones: [],
      setNotificaciones: () => {},
      setContadorNoLeidas: () => {},
      loading: false,
      setLoading: () => {},
      fetchContador: async () => {},
      notifyRefresh: () => {}
    }
  }
  return context
}

// Hook completo para usar el contexto de notificaciones
export const useNotifications = () => {
  const context = useContext(NotificationContext)
  if (!context) {
    throw new Error('useNotifications debe usarse dentro de NotificationProvider')
  }
  return context
}

